import type {
  CallAnalysisTagDefinitionResponse,
  CreateCallAnalysisTagDefinitionRequest,
  ListCallAnalysisTagDefinitionsResponse,
  UpdateCallAnalysisTagDefinitionRequest,
} from '@reach/shared-types/call-analysis-tag-definitions';
import { apiRequest } from '../../../lib/api-utils';

type ApiResponse<T> = { data: T };

const BASE = '/partner/voice/call-analysis-tags';

const FETCH_PAGE_LIMIT = 100;

/**
 * Loads every tag definition for the current partner by following cursor pages.
 */
export async function fetchTagDefinitions(
  includeArchived = false,
): Promise<CallAnalysisTagDefinitionResponse[]> {
  const all: CallAnalysisTagDefinitionResponse[] = [];
  let cursor: string | undefined;

  for (;;) {
    const params = new URLSearchParams();
    params.set('limit', String(FETCH_PAGE_LIMIT));
    if (includeArchived) {
      params.set('includeArchived', 'true');
    }
    if (cursor) {
      params.set('cursor', cursor);
    }

    const response = await apiRequest<ApiResponse<ListCallAnalysisTagDefinitionsResponse>>(
      `${BASE}?${params.toString()}`,
    );
    all.push(...response.data.results);
    if (!response.data.pagination.hasNextPage || response.data.pagination.cursor == null) {
      break;
    }
    cursor = response.data.pagination.cursor;
  }

  return all;
}

export async function createTagDefinition(
  body: CreateCallAnalysisTagDefinitionRequest,
): Promise<CallAnalysisTagDefinitionResponse> {
  const response = await apiRequest<ApiResponse<CallAnalysisTagDefinitionResponse>>(BASE, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return response.data;
}

export async function updateTagDefinition(
  id: string,
  body: UpdateCallAnalysisTagDefinitionRequest,
): Promise<CallAnalysisTagDefinitionResponse> {
  const response = await apiRequest<ApiResponse<CallAnalysisTagDefinitionResponse>>(`${BASE}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  return response.data;
}

export async function archiveTagDefinition(id: string): Promise<void> {
  await apiRequest<ApiResponse<{ message: string }>>(`${BASE}/${id}`, {
    method: 'DELETE',
  });
}
